import React, { useState, useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import WelcomeCard from './components/WelcomeCard';
import QuickActionCard from './components/QuickActionCard';
import OpportunityCard from './components/OpportunityCard';
import UpcomingEventsCard from './components/UpcomingEventsCard';
import ImpactMetricsCard from './components/ImpactMetricsCard';
import ActivityFeedCard from './components/ActivityFeedCard';
import SkillProgressCard from './components/SkillProgressCard';
import CommunityUpdatesCard from './components/CommunityUpdatesCard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';


const MissionControlDashboard = () => {
  const [userType, setUserType] = useState('volunteer');
  const [isCustomizing, setIsCustomizing] = useState(false);

  const { user: authUser } = useAuth();

  // Default Data for Fresh Accounts (or fallbacks)
  const defaultStats = {
    totalHours: 0,
    completed: 0,
    impactScore: 0,
    monthlyHours: 0
  };

  const displayName = authUser?.name || "Volunteer";

  // Priority: DB Data -> Default Empty
  // Note: authController returns numbers, so 0 is falsy, check for undefined/null explicitly if needed,
  // but usually || works fine if we want 0 to be 0.
  const currentStats = authUser?.impactStats || defaultStats;
  const currentSkills = authUser?.skills || []; // Empty for new users

  // Create a welcome activity for everyone so it's not empty
  const currentActivities = [
    {
      id: 'welcome',
      userName: "System",
      userAvatar: null,
      userAvatarAlt: null,
      action: "welcomed you to",
      details: "VolunteerHub! Start your journey today.",
      timestamp: new Date(),
      type: "milestone",
      badge: "Welcome"
    }
  ];

  /* 
     We are keeping mockOpportunities/Events/Metrics/Updates below 
     because those are "Global" app data, not user specific.
  */

  const mockOpportunities = [
    {
      id: 1,
      title: "Social Media Campaign Manager",
      organization: "Green Earth Initiative",
      organizationLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_11d2a1522-1764749860533.png",
      organizationLogoAlt: "Green Earth Initiative logo showing stylized green leaf and earth symbol on white background",
      location: "Remote",
      commitment: "10 hrs/week",
      matchScore: 95,
      skills: ["Marketing", "Design"]
    },
    {
      id: 2,
      title: "Youth Coding Instructor",
      organization: "Tech for Tomorrow",
      organizationLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9d8352a-1764640192706.png",
      organizationLogoAlt: "Tech for Tomorrow logo featuring modern circuit board pattern with blue and orange gradient",
      location: "Downtown Center",
      commitment: "6 hrs/week",
      matchScore: 88,
      skills: ["Teaching", "Technology"]
    },
    {
      id: 3,
      title: "Community Health Educator",
      organization: "Wellness Warriors",
      organizationLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb8d7e4e-1764634734332.png",
      organizationLogoAlt: "Wellness Warriors logo with red cross symbol and heart icon on clean white medical background",
      location: "Multiple Locations",
      commitment: "8 hrs/week",
      matchScore: 82,
      skills: ["Healthcare", "Teaching"]
    }];


  const mockEvents = [
    {
      id: 1,
      title: "Community Food Drive",
      date: "2025-12-20T10:00:00",
      location: "City Park",
      attendees: 45,
      spotsLeft: 5
    },
    {
      id: 2,
      title: "Beach Cleanup Initiative",
      date: "2025-12-22T08:00:00",
      location: "Sunset Beach",
      attendees: 32,
      spotsLeft: 18
    },
    {
      id: 3,
      title: "Skills Workshop: Grant Writing",
      date: "2025-12-25T14:00:00",
      location: "Virtual Event",
      attendees: 67,
      spotsLeft: null
    }];


  const mockMetrics = [
    {
      label: "Total Volunteers",
      value: "12,458",
      progress: 78,
      description: "Active community members"
    },
    {
      label: "Organizations Served",
      value: "342",
      progress: 65,
      description: "Nonprofit partnerships"
    },
    {
      label: "Skills Matched",
      value: "8,921",
      progress: 92,
      description: "Successful connections"
    },
    {
      label: "Community Impact",
      value: "156K",
      progress: 85,
      description: "Total volunteer hours"
    }];


  // Mock activities removed as they are now handled by currentActivities logic or fallback above
  // We can keep a small static fallback list within mockUserData if we want, or just delete the big block below.
  // For clarity, I'm replacing the entire block of mockActivities and mockSkills variables with nothing,
  // as they are now effectively inside mockUserData/userData.



  const mockUpdates = [
    {
      id: 1,
      type: "milestone",
      title: "VolunteerHub Reaches 10,000 Active Volunteers!",
      description: "We're thrilled to announce that our community has grown to over 10,000 active volunteers making a difference across 500+ organizations. Thank you for being part of this incredible movement!",
      date: "2025-12-15T09:00:00",
      image: "https://images.unsplash.com/photo-1554784025-7895bad08939",
      imageAlt: "Large diverse group of volunteers celebrating together outdoors with raised hands and colorful balloons against blue sky",
      likes: 342,
      comments: 67,
      shares: 89
    },
    {
      id: 2,
      type: "announcement",
      title: "New Skills Academy Courses Available",
      description: "Expand your capabilities with our latest certification programs in Grant Writing, Nonprofit Leadership, and Community Engagement. Enroll now and earn professional development credits.",
      date: "2025-12-14T14:30:00",
      image: null,
      imageAlt: null,
      likes: 156,
      comments: 23,
      shares: 45
    },
    {
      id: 3,
      type: "success",
      title: "Community Impact: 50,000 Hours Milestone",
      description: "Our volunteers have collectively contributed over 50,000 hours this quarter, directly impacting 15,000 community members. Your dedication is transforming lives!",
      date: "2025-12-13T11:00:00",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2ea6a0b-1764653913814.png",
      imageAlt: "Volunteers working together at community food bank sorting and organizing donated food items in bright warehouse space",
      likes: 289,
      comments: 45,
      shares: 112
    },
    {
      id: 4,
      type: "news",
      title: "Partnership Announcement: Tech Giants Join Forces",
      description: "We're excited to welcome three major tech companies to our corporate partnership program, bringing 5,000 new employee volunteers and $2M in matching grants to support local nonprofits.",
      date: "2025-12-12T16:00:00",
      image: null,
      imageAlt: null,
      likes: 198,
      comments: 34,
      shares: 67
    }];


  useEffect(() => {
    const savedUserType = localStorage.getItem('volunteerHubUserType');
    if (savedUserType) {
      setUserType(savedUserType);
    }
  }, []);

  const handleUserTypeToggle = () => {
    const newType = userType === 'volunteer' ? 'organization' : 'volunteer';
    setUserType(newType);
    localStorage.setItem('volunteerHubUserType', newType);
  };

  return (
    <PageLayout>
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Mission Control Dashboard
          </h1>
          <p className="text-muted-foreground">
            Your personalized hub for making meaningful impact
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Demo button removed */}
          {/* Customize button removed */}
        </div>
      </div>

      {isCustomizing &&
        <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                Customization Mode Active
              </p>
              <p className="text-sm text-muted-foreground">
                Drag and drop cards to rearrange your dashboard. Your preferences will be saved automatically.
              </p>
            </div>
          </div>
        </div>
      }

      <div className="space-y-6">
        <WelcomeCard
          userName={displayName}
          impactStats={currentStats} />


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <QuickActionCard userType={userType} />
            <OpportunityCard opportunities={mockOpportunities} />
            <ImpactMetricsCard metrics={mockMetrics} />
          </div>

          <div className="space-y-6">
            <UpcomingEventsCard events={mockEvents} />
            <SkillProgressCard skills={currentSkills} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ActivityFeedCard activities={currentActivities} />
          <CommunityUpdatesCard updates={mockUpdates} />
        </div>
      </div>
    </PageLayout>);

};

export default MissionControlDashboard;